import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

/**
 * Chat Store
 * Manages conversations, messages, and chat-related state
 */
export const useChatStore = create(
  devtools(
    set => ({
      conversations: [],
      selectedConversation: null,
      messages: [],
      newMessage: '',
      searchQuery: '',

      // Loading states
      isLoadingConversations: false,
      isLoadingMessages: false,
      isSendingMessage: false,

      // Message interaction states
      copiedMessageId: null,
      regeneratingMessageId: null,
      streamingMessageId: null,

      // UI states
      error: '',
      lastTokenUsage: null,
      currentThinkingStatus: '',
      chatElapsedSeconds: 0,

      // Scroll states
      userHasScrolledUp: false,
      showGoToTop: false,

      // Conversations
      setConversations: conversations => set({ conversations }),

      setSelectedConversation: conversation =>
        set({ selectedConversation: conversation }),

      updateConversation: (conversationId, updates) =>
        set(state => ({
          conversations: state.conversations.map(conv =>
            conv.id === conversationId ? { ...conv, ...updates } : conv
          ),
          selectedConversation:
            state.selectedConversation?.id === conversationId
              ? { ...state.selectedConversation, ...updates }
              : state.selectedConversation,
        })),

      addConversation: conversation =>
        set(state => ({
          conversations: [conversation, ...state.conversations].sort(
            (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
          ),
        })),

      removeConversation: conversationId =>
        set(state => ({
          conversations: state.conversations.filter(conv => conv.id !== conversationId),
          selectedConversation:
            state.selectedConversation?.id === conversationId
              ? null
              : state.selectedConversation,
        })),

      clearAllConversations: () =>
        set({ conversations: [], selectedConversation: null, messages: [] }),

      // Messages
      setMessages: messages => set({ messages }),

      addMessage: message =>
        set(state => ({
          messages: [...state.messages, message],
        })),

      updateMessage: (messageId, updates) =>
        set(state => ({
          messages: state.messages.map(msg =>
            msg.message_id === messageId ? { ...msg, ...updates } : msg
          ),
        })),

      setMessageMetadata: (messageId, metadataUpdates) =>
        set(state => ({
          messages: state.messages.map(msg =>
            msg.message_id === messageId
              ? {
                  ...msg,
                  metadata: {
                    ...(msg.metadata || {}),
                    ...metadataUpdates,
                  },
                }
              : msg
          ),
        })),

      removeMessage: messageId =>
        set(state => ({
          messages: state.messages.filter(msg => msg.message_id !== messageId),
        })),

      replaceOptimisticMessage: (tempId, actualMessage) =>
        set(state => ({
          messages: state.messages.map(msg =>
            msg.message_id === tempId ? actualMessage : msg
          ),
        })),

      // Input
      setNewMessage: message => set({ newMessage: message }),

      clearNewMessage: () => set({ newMessage: '' }),

      setSearchQuery: query => set({ searchQuery: query }),

      // Loading states
      setIsLoadingConversations: loading => set({ isLoadingConversations: loading }),

      setIsLoadingMessages: loading => set({ isLoadingMessages: loading }),

      setIsSendingMessage: sending => set({ isSendingMessage: sending }),

      // Message interactions
      setCopiedMessageId: id => set({ copiedMessageId: id }),

      setRegeneratingMessageId: id => set({ regeneratingMessageId: id }),

      setStreamingMessageId: id => set({ streamingMessageId: id }),

      // UI states
      setError: error => set({ error }),

      clearError: () => set({ error: '' }),

      setLastTokenUsage: usage => set({ lastTokenUsage: usage }),

      setCurrentThinkingStatus: status => set({ currentThinkingStatus: status }),

      setChatElapsedSeconds: seconds => set({ chatElapsedSeconds: seconds }),

      incrementChatElapsedSeconds: () =>
        set(state => ({ chatElapsedSeconds: state.chatElapsedSeconds + 1 })),

      // Scroll states
      setUserHasScrolledUp: scrolledUp => set({ userHasScrolledUp: scrolledUp }),

      setShowGoToTop: show => set({ showGoToTop: show }),

      // Reset operations
      resetChatState: () =>
        set({
          selectedConversation: null,
          messages: [],
          newMessage: '',
          error: '',
          lastTokenUsage: null,
          currentThinkingStatus: '',
          streamingMessageId: null,
          regeneratingMessageId: null,
          chatElapsedSeconds: 0,
        }),

      resetMessageInteractions: () =>
        set({
          copiedMessageId: null,
          regeneratingMessageId: null,
          streamingMessageId: null,
        }),
    }),
    { name: 'ChatStore' }
  )
)
